"use client";

import Link from "next/link";
import { Input } from "@/components/ui/input";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import AuthLayout, { AuthButton } from "../AuthLayout";
import { fetchApi, handleApiError } from "@/lib/api";
import { useRouter } from "next/navigation";

export default function SignupPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const router = useRouter();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const response = await fetchApi("/auth/signup/", {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
        }),
      });
      router.push("/auth/signin");
    } catch (error) {
      console.error("Signup failed:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "Sign up failed"
      );
    }
  };

  return (
    <AuthLayout
      title="Sign Up"
      subtitle="Get your insights in hours instead of weeks."
      onSubmit={handleSubmit}
    >
      <div className="space-y-2">
        <Input
          id="email"
          placeholder="Your email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />
      </div>
      <div className="space-y-2">
        <div className="relative">
          <Input
            id="password"
            placeholder="Password"
            type={showPassword ? "text" : "password"}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? (
              <EyeOffIcon className="h-4 w-4" />
            ) : (
              <EyeIcon className="h-4 w-4" />
            )}
            <span className="sr-only">
              {showPassword ? "Hide password" : "Show password"}
            </span>
          </button>
        </div>
      </div>
      <AuthButton type="submit">Sign Up</AuthButton>
      <div className="text-center text-sm">
        Already have an account?{" "}
        <Link
          href="/auth/signin"
          className="text-gray-900 hover:text-gray-700 underline"
        >
          Sign In
        </Link>
      </div>
      {successMessage && <div className="text-green-500">{successMessage}</div>}
      {errorMessage && <div className="text-red-500">{errorMessage}</div>}
    </AuthLayout>
  );
}
